import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_TEMPLATE = {
  name: '',
  program_name: '',
  default_course_name: '',
  default_instructor_name: '',
  default_instructional_hours: 40,
  certificate_title: '',
  seal_url: '',
  instructor_signature_url: '',
  kra_signature_url: '',
  is_default: false,
  is_active: true,
};

const EMPTY_CERTIFICATE = {
  student_id: '',
  program_name: '',
  course_id: '',
  course_name: '',
  completion_date: '',
  instructor_name: '',
  instructional_hours: 40,
  template_id: '',
  seal_url: '',
  instructor_signature_url: '',
  kra_signature_url: '',
};

export default function AdminCertificates() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [templates, setTemplates] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [templateForm, setTemplateForm] = useState(EMPTY_TEMPLATE);
  const [certificateForm, setCertificateForm] = useState(EMPTY_CERTIFICATE);
  const [search, setSearch] = useState('');

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [templatesRes, certificatesRes] = await Promise.all([
        axios.get(`${API}/admin/certificates/templates`),
        axios.get(`${API}/admin/certificates`, { params: { limit: 300 } }),
      ]);
      setTemplates(templatesRes.data?.templates || []);
      setCertificates(certificatesRes.data?.certificates || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load certificate admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const createTemplate = async () => {
    setStatus('');
    setError('');
    try {
      await axios.post(`${API}/admin/certificates/templates`, {
        ...templateForm,
        default_instructional_hours: Number(templateForm.default_instructional_hours || 40),
      });
      setTemplateForm(EMPTY_TEMPLATE);
      setStatus('Template created');
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to create template');
    }
  };

  const generateCertificate = async () => {
    setStatus('');
    setError('');
    try {
      await axios.post(`${API}/admin/certificates/generate`, {
        ...certificateForm,
        course_id: certificateForm.course_id || undefined,
        course_name: certificateForm.course_name || undefined,
        completion_date: certificateForm.completion_date || undefined,
        instructor_name: certificateForm.instructor_name || undefined,
        instructional_hours: Number(certificateForm.instructional_hours || 40),
        template_id: certificateForm.template_id || undefined,
        seal_url: certificateForm.seal_url || undefined,
        instructor_signature_url: certificateForm.instructor_signature_url || undefined,
        kra_signature_url: certificateForm.kra_signature_url || undefined,
      });
      setCertificateForm(EMPTY_CERTIFICATE);
      setStatus('Certificate generated');
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to generate certificate');
    }
  };

  const revokeCertificate = async (certificateId) => {
    const reason = window.prompt('Enter revoke reason (optional)') || '';
    setStatus('');
    setError('');
    try {
      await axios.post(`${API}/admin/certificates/${certificateId}/revoke`, { reason });
      setStatus(`Certificate ${certificateId} revoked`);
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to revoke certificate');
    }
  };

  const filteredCertificates = (certificates || []).filter((item) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [
      item.certificate_id,
      item.student_full_name,
      item.program_name,
      item.course_name,
      item.status,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(q);
  });

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto p-6 space-y-8">
        <h1 className="text-3xl font-bold text-yellow-400">Admin Certificate Management</h1>

        <div className="flex items-center gap-3">
          <button onClick={loadAll} className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded">Refresh</button>
          {loading && <span className="text-gray-400">Loading...</span>}
          {status && <span className="text-green-400">{status}</span>}
          {error && <span className="text-red-400">{error}</span>}
        </div>

        <section className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-4">
          <h2 className="text-xl font-bold text-yellow-400">Create Certificate Template</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Template name" />
            <input value={templateForm.program_name} onChange={(e) => setTemplateForm({ ...templateForm, program_name: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Program name" />
            <input value={templateForm.default_course_name} onChange={(e) => setTemplateForm({ ...templateForm, default_course_name: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Default course name" />
            <input value={templateForm.default_instructor_name} onChange={(e) => setTemplateForm({ ...templateForm, default_instructor_name: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Default instructor" />
            <input type="number" value={templateForm.default_instructional_hours} onChange={(e) => setTemplateForm({ ...templateForm, default_instructional_hours: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Default hours" />
            <input value={templateForm.certificate_title} onChange={(e) => setTemplateForm({ ...templateForm, certificate_title: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Certificate title" />
            <input value={templateForm.seal_url} onChange={(e) => setTemplateForm({ ...templateForm, seal_url: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="K.R.A seal URL" />
            <input value={templateForm.instructor_signature_url} onChange={(e) => setTemplateForm({ ...templateForm, instructor_signature_url: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Instructor signature URL" />
            <input value={templateForm.kra_signature_url} onChange={(e) => setTemplateForm({ ...templateForm, kra_signature_url: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="K.R.A signature URL" />
          </div>
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={templateForm.is_default} onChange={(e) => setTemplateForm({ ...templateForm, is_default: e.target.checked })} /> Default template</label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={templateForm.is_active} onChange={(e) => setTemplateForm({ ...templateForm, is_active: e.target.checked })} /> Active</label>
          </div>
          <button onClick={createTemplate} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded font-bold">Create Template</button>
        </section>

        <section className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-4">
          <h2 className="text-xl font-bold text-yellow-400">Generate Certificate</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input value={certificateForm.student_id} onChange={(e) => setCertificateForm({ ...certificateForm, student_id: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Student ID" />
            <input value={certificateForm.program_name} onChange={(e) => setCertificateForm({ ...certificateForm, program_name: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Program name" />
            <input value={certificateForm.course_id} onChange={(e) => setCertificateForm({ ...certificateForm, course_id: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Course ID (optional)" />
            <input value={certificateForm.course_name} onChange={(e) => setCertificateForm({ ...certificateForm, course_name: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Course name (optional)" />
            <input type="date" value={certificateForm.completion_date} onChange={(e) => setCertificateForm({ ...certificateForm, completion_date: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" />
            <input value={certificateForm.instructor_name} onChange={(e) => setCertificateForm({ ...certificateForm, instructor_name: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Instructor name" />
            <input type="number" value={certificateForm.instructional_hours} onChange={(e) => setCertificateForm({ ...certificateForm, instructional_hours: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600" placeholder="Instructional hours" />
            <select value={certificateForm.template_id} onChange={(e) => setCertificateForm({ ...certificateForm, template_id: e.target.value })} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600">
              <option value="">Use default template</option>
              {(templates || []).map((template) => (
                <option key={template.id} value={template.id}>{template.name} ({template.program_name})</option>
              ))}
            </select>
          </div>
          <button onClick={generateCertificate} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded font-bold">Generate Certificate</button>
        </section>

        <section className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
            <h2 className="text-xl font-bold text-yellow-400">Issued Certificates</h2>
            <input value={search} onChange={(e) => setSearch(e.target.value)} className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600 md:w-80" placeholder="Search certificates" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredCertificates.map((item) => (
              <div key={item.id} className="bg-gray-800 border border-yellow-400/10 rounded-xl p-4 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="text-yellow-400 font-semibold">{item.certificate_id}</div>
                  <span className={`text-xs px-2 py-1 rounded ${item.status === 'valid' ? 'bg-green-700 text-green-100' : 'bg-red-700 text-red-100'}`}>{item.status}</span>
                </div>
                <div className="text-white">{item.student_full_name}</div>
                <div className="text-gray-300 text-sm">{item.program_name} • {item.course_name}</div>
                <div className="text-gray-400 text-xs">Completion: {item.completion_date || 'N/A'} • Hours: {item.instructional_hours || 'N/A'}</div>
                <div className="text-gray-400 text-xs">Instructor: {item.instructor_name || 'N/A'}</div>
                <div className="flex flex-wrap gap-3 pt-2">
                  {item.verification_url && <a href={item.verification_url} target="_blank" rel="noreferrer" className="text-yellow-400 text-xs hover:text-yellow-300">Open Verification</a>}
                  {item.qr_code_url && <a href={item.qr_code_url} target="_blank" rel="noreferrer" className="text-yellow-400 text-xs hover:text-yellow-300">Open QR</a>}
                  {item.status === 'valid' && (
                    <button onClick={() => revokeCertificate(item.certificate_id)} className="text-red-400 text-xs hover:text-red-300">Revoke</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
